import { useQuery } from '@tanstack/react-query';
import { axiosGet, axiosGetV3 } from '../../api/axios-handler';
import useAuth from '../utils/useAuth';
import { AxiosError } from 'axios';

export type ProviderName = 'recuro' | 'opyn' | 'dialcare' | 'me_md' | 'revive';
export type ProviderType = 'primary' | 'counseling' | 'urgent' | 'crisis' | 'crisis_lite' | 'rx' | 'pediatric';

export interface ProviderTypeConfig {
  id?: number;
  provider_type: ProviderType;
  content_html?: string | null;
  tile_name?: string | null;
  tile_description?: string | null;
  tile_image_url?: string | null;
  tile_tags?: string[] | null;
  online_access_url?: string | null;
}

export interface ExternalProviderPlan {
  name: string;
  provider: ProviderName;
  id: number;
  display_name: string;
  code: string;
  provider_types: ProviderType[];
  type_configs?: ProviderTypeConfig[];
}
const getExternalProviderPlan = async (): Promise<ExternalProviderPlan[]> => {
  return await axiosGetV3(`/external_provider_plans/`, {}).then((resourceResponse) => resourceResponse.data);
};

export const useExternalProviderPlan = (enabled = true) =>
  useQuery<ExternalProviderPlan[]>(['externalProviderPlan'], () => getExternalProviderPlan(), {
    staleTime: Infinity,
    enabled,
  });

export interface RedirectUrl {
  redirect_url: string;
}

const getSsoRedirect = async (userId: number, providerName: string): Promise<RedirectUrl> => {
  return await axiosGet(`/users/${userId}/sso_redirect/${providerName}`, {})
    .then((resourceResponse) => resourceResponse.data)
    .catch((err: AxiosError) => {
      // TODO: Clean this up once we refactor how we handle errors in the backend
      throw err;
    });
};

export const useSsoRedirect = (userId: number, enabled: boolean, providerType: string) => {
  return useQuery<RedirectUrl>(['ssoRedirect', userId, providerType], () => getSsoRedirect(userId, providerType), {
    enabled: enabled,
  });
};

interface ProviderPlanData {
  providerPlan: ExternalProviderPlan | null;
  providerName: ProviderName | undefined;
  providerTypes: ProviderType[] | undefined;
  isFetching: boolean;
  isLoading: boolean;
}

export const useProviderPlan = (): ProviderPlanData => {
  const { user } = useAuth();
  const { data: providerPlans, isFetching, isLoading } = useExternalProviderPlan(Boolean(user));

  const providerPlan =
    providerPlans?.find((plan) => plan.id === user?.caller_role.active_subscription?.package.external_plan_id) ?? null;
  const providerName = providerPlan?.provider;
  const providerTypes = providerPlan?.provider_types;

  return {
    providerPlan: providerPlan,
    providerName: providerName,
    providerTypes: providerTypes,
    isFetching,
    isLoading,
  };
};
